import {map} from 'lodash';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
    Accordion,
    AccordionDetails,
    AccordionTitle,
    ConfirmModal,
    DataWrapper,
    Icon, IconColor, IconName, IconSize, PredefinedNotifierType,
    Table, TableBody, TableCell,
    TableIdCell, TableRow, Tooltip
} from 'components';
import {UserContext} from 'context';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {
    OutgoingMessage, OutgoingMessageChannel, OutgoingMessageStatus, PaginatedData, UserSummary
} from 'types';
import {dateUtils} from 'utils';
import {buildTranslationKey, findUserName, formatDate} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.OUTGOING_MESSAGES;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

const CHANNEL_ICONS: {[key in OutgoingMessageChannel]: IconName} = {
    [OutgoingMessageChannel.Sms]: 'chat2',
    [OutgoingMessageChannel.Email]: 'mail',
    [OutgoingMessageChannel.Whatsapp]: 'whatsapp' // TODO: Icon
};

const STATUS_ICONS: {[key in OutgoingMessageStatus]: IconName} = {
    [OutgoingMessageStatus.Pending]: 'warning',
    [OutgoingMessageStatus.Sent]: 'check-circle',
    [OutgoingMessageStatus.Error]: 'x-circle',
};

const STATUS_ICON_COLORS: {[key in OutgoingMessageStatus]: IconColor} = {
    [OutgoingMessageStatus.Pending]: 'default',
    [OutgoingMessageStatus.Sent]: 'success',
    [OutgoingMessageStatus.Error]: 'alert',
};

type Props = {
    users: UserSummary[];
    isHistory?: boolean;
    canManage?: boolean;
    onItemsCountUpdate: (count: number) => void;
    getFn: () => Promise<PaginatedData<OutgoingMessage>>;
    removeFn: (id: string) => Promise<void>;
}

export const OutgoingMessagesList: React.FC<Props> = ({getFn, canManage, isHistory, users = [], onItemsCountUpdate, removeFn}) => {
    const {t} = useTranslation();

    const {send: fetchMessages, data, loading} = useApiData(getFn, {notificationType: PredefinedNotifierType.FetchError});
    const {send: removeMessage} = useApiData(removeFn, {notificationType: PredefinedNotifierType.Delete, onSuccess: fetchMessages});
    const [confirmDeleteModalProps, setConfirmDeleteModalProps] = useState<{open: boolean, id?: string}>({open: false});

    const handleConfirmDeleteModalClose = useCallback((confirm: boolean) => {
        if (confirm && confirmDeleteModalProps?.id) {
            removeMessage(confirmDeleteModalProps.id);
        }
        setConfirmDeleteModalProps({open: false});
    }, [removeMessage, confirmDeleteModalProps]);

    const {settings: {common: {timeZone, dateFormat, timeFormat}}} = useContext(UserContext);
    const messages = useMemo(() => data?.items || [], [data]);

    const headers = useMemo(() => [
        {children: t(COMMON_T_KEYS.SESSION.LABELS.ID), className: 'tw-w-24'},
        {children: t(T_KEYS.FIELDS.FEATURE), className: 'tw-w-32'},
        {children: t(T_KEYS.FIELDS.USER), className: 'tw-w-32'},
        {children: t(T_KEYS.FIELDS.RECIPIENT), className: 'tw-w-32'},
        {children: t(T_KEYS.FIELDS.CONTENT), className: 'tw-w-8'},
        ...(isHistory ? [{children: t(T_KEYS.FIELDS.STATUS), className: 'tw-w-8'}] : []),
        ...(canManage ? [{children: t(T_KEYS.FIELDS.ACTIONS), className: 'tw-w-8'}] : []),
    ], [t, isHistory, canManage]);

    const renderRecipient = useCallback((m: OutgoingMessage) => {
        const iconName: IconName = CHANNEL_ICONS[m.channel] || 'x-circle';
        const header = t(COMMON_T_KEYS.OUTGOING_MESSAGE_CHANNEL[buildTranslationKey(m.channel)]);
        const recipient = m.recipient ?? '';
        const recipientFormatted = recipient.length > 25 ? `${recipient.slice(0, 12)}...${recipient.slice(-10)}` : recipient;
        return (
            <div className='tw-flex tw-items-center'>
                <Tooltip header={header}>
                    <Icon
                        name={iconName}
                        size='sm'
                        className='tw-mr-2'
                    />
                </Tooltip>
                <div>{recipientFormatted}</div>
            </div>
        );
    }, [t]);

    const renderFeature = useCallback((m: OutgoingMessage) =>
        t(COMMON_T_KEYS.OUTGOING_MESSAGE_FEATURE[buildTranslationKey(m.feature)]), [t]);

    const renderContent = useCallback((m: OutgoingMessage, size: IconSize = 'md') => {
        // Sanitize done on backend
        // Consider better approach
        const label = m.channel === OutgoingMessageChannel.Email ? <div dangerouslySetInnerHTML={{__html: m.content}} /> : m.content;
        return (
            <Tooltip message={label}>
                <Icon name='eye' className='tw-ml-2' size={size} />
            </Tooltip>
        );
    }, []);

    const renderUser = useCallback((m: OutgoingMessage) => (
        <div className='tw-flex tw-items-center'>
            {findUserName(t, users, {eid: m.createdBy}) || m.createdBy}
        </div>
    ), [t, users]);

    const renderStatus = useCallback((m: OutgoingMessage, size: IconSize = 'lg') => {
        const iconName = STATUS_ICONS[m.status];
        const color = STATUS_ICON_COLORS[m.status];
        const header = t(COMMON_T_KEYS.OUTGOING_MESSAGE_STATUS[buildTranslationKey(m.status)]);
        const statusDetails = m.statusDetails ?
            t(COMMON_T_KEYS.OUTGOING_MESSAGE_STATUS_DETAILS[buildTranslationKey(m.statusDetails)]) : null;

        return (
            <Tooltip header={header} message={statusDetails}>
                <Icon name={iconName} color={color} size={size} thickness='sm' />
            </Tooltip>
        );
    }, [t]);

    const renderRemoveButton = useCallback((m: OutgoingMessage) => (
        <Icon
            button
            color='alert'
            name='delete'
            onClick={() => setConfirmDeleteModalProps({open: true, id: m.id})}
        />
    ), []);

    useEffect(() => {
        fetchMessages();
    }, [fetchMessages]);

    useEffect(() => {
        onItemsCountUpdate(data?.count || 0);
    }, [data, onItemsCountUpdate]);

    return (
        <div className='tw-relative'>
            <Table
                headers={headers}
                noData={!messages.length}
                loading={loading}
                className='-md:tw-hidden'
            >
                <TableBody>
                    {map(messages, (m: OutgoingMessage) => (
                        <TableRow key={m.id}>
                            <TableIdCell
                                item={m}
                                timeZone={timeZone}
                                dateFormat={dateFormat}
                                timeFormat={timeFormat}
                                className='tw-text-default-dimmed tw-text-nowrap'
                            />
                            <TableCell>
                                {renderFeature(m)}
                            </TableCell>
                            <TableCell>
                                {renderUser(m)}
                            </TableCell>
                            <TableCell ellipsis>
                                {renderRecipient(m)}
                            </TableCell>
                            <TableCell>
                                {renderContent(m)}
                            </TableCell>
                            {isHistory && (
                                <TableCell>
                                    {renderStatus(m)}
                                </TableCell>
                            )}
                            {canManage && (
                                <TableCell>
                                    {renderRemoveButton(m)}
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <div className='md:tw-hidden'>
                <DataWrapper isLoading={loading} noData={!messages.length}>
                    {map(messages, (m: OutgoingMessage) => (
                        <Accordion key={m.id} className='tw-min-w-48 tw-mb-2'>
                            <AccordionTitle>
                                <div className='tw-flex tw-items-center tw-gap-1'>
                                    {renderRecipient(m)}
                                    {isHistory && (
                                        <div className='tw-ml-2'>{renderStatus(m, 'md')}</div>
                                    )}
                                    {canManage && (
                                        <div className='tw-ml-auto -sm:tw-mr-2'>
                                            {renderRemoveButton(m)}
                                        </div>
                                    )}
                                </div>
                                <div className='tw-text-default-dimmed tw-text-sm'>
                                    {formatDate(
                                        new Date(m.createdAt),
                                        timeZone,
                                        dateUtils.getDateTimeFormat(dateFormat, timeFormat)
                                    )}
                                </div>
                            </AccordionTitle>
                            <AccordionDetails className='tw-flex tw-items-center'>
                                <div className='tw-mr-4'>{renderUser(m)}</div>
                                <div>{renderFeature(m)}</div>
                                <div className='tw-ml-4'>{renderContent(m)}</div>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </DataWrapper>

            </div>
            <ConfirmModal {...confirmDeleteModalProps} title={t(T_KEYS.REMOVE_MODAL.TITLE)} onClose={handleConfirmDeleteModalClose} />
        </div>
    );
};